import React, { useEffect, useState } from 'react';
import { Box, Typography, List, ListItem, ListItemText, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

interface ActiveIngredientDetail {
  activeIngredientName: string;
  unitAmount: number;
  unitName: string;
}

// Shows a Product's active-ingredient composition/concentration (e.g.
// "Glyphosate — 470 g/L") so an admin mapping a raw import product name to
// this Product can visually confirm it's the right one — many products
// share near-identical names and are only distinguished by strength.
const ProductActiveIngredientsPanel: React.FC<{ productId?: string }> = ({ productId }) => {
  const auth = useAuth();
  const [ingredients, setIngredients] = useState<ActiveIngredientDetail[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!productId || !auth.tokens?.accessToken) {
      setIngredients(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(`/api/products/${productId}`, { headers: { Authorization: `Bearer ${auth.tokens.accessToken}` } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled) setIngredients(data?.activeIngredients ?? []);
      })
      .catch(() => {
        if (!cancelled) setIngredients([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId, auth.tokens?.accessToken]);

  if (!productId) {
    return null;
  }

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Active Ingredients
      </Typography>
      {loading ? (
        <CircularProgress size={20} />
      ) : !ingredients || ingredients.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No active ingredients recorded for this product.
        </Typography>
      ) : (
        <List dense disablePadding>
          {ingredients.map((ai, index) => (
            <ListItem key={index} disableGutters sx={{ py: 0.25 }}>
              <ListItemText primary={`${ai.activeIngredientName} — ${ai.unitAmount} ${ai.unitName}`} />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default ProductActiveIngredientsPanel;
